import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { MaterialIcon } from '@/components/ui/MaterialIcon';
import SEO from '@/components/layout/SEO';

const maintenanceSchema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'startzone.dev',
    url: 'https://startzone.dev',
    description: 'startzone.dev is undergoing scheduled maintenance. Back online soon.',
});

const STATUS_ROWS = [
    { key: 'CORE_TEMP', value: '42.8°C', color: 'text-secondary' },
    { key: 'NODE_LOAD', value: 'CRITICAL', color: 'text-tertiary' },
    { key: 'REFACTOR', value: 'IN_PROGRESS', color: 'text-primary' },
];

const BOOT_LOG = [
    '// BOOT_SEQUENCE: 0x99A1',
    '// KERNEL_HASH: F8:32:01:EE',
    '// ARCH: X64_CYBER_ZEN',
    '// STATUS: REFACTOR_IN_PROGRESS',
];

export default function MaintenancePage() {
    return (
        <div className="relative min-h-screen overflow-hidden bg-[#0e0e0e] text-white">
            <SEO schema={maintenanceSchema} title="startzone.dev — System Upgrade in Progress" />
            <Navbar />

            <div
                className="pointer-events-none fixed inset-0 z-0 opacity-10"
                style={{ backgroundImage: 'radial-gradient(#8cacff 0.5px, transparent 0.5px)', backgroundSize: '24px 24px' }}
            />
            <div className="pointer-events-none fixed inset-0 z-0 opacity-20" style={{ background: 'linear-gradient(to bottom, rgba(140,172,255,0.1) 50%, transparent 50%)', backgroundSize: '100% 4px' }} />
            <div className="pointer-events-none fixed inset-0 z-0">
                <div className="absolute top-0 left-1/2 h-[400px] w-[700px] -translate-x-1/2 bg-[radial-gradient(circle,rgba(140,172,255,0.08),transparent_60%)]" />
                <div className="absolute bottom-0 right-0 h-[400px] w-[500px] bg-[radial-gradient(circle,rgba(214,116,255,0.06),transparent_60%)] blur-3xl" />
            </div>

            <div className="pointer-events-none absolute left-12 top-24 hidden select-none font-mono text-[10px] uppercase leading-relaxed tracking-widest text-[#484848] lg:block">
                {BOOT_LOG.map((line) => (
                    <div key={line}>{line}</div>
                ))}
            </div>

            <div className="pointer-events-none absolute bottom-12 right-12 hidden gap-4 lg:flex">
                {['A1', 'B4', 'Z9'].map((label) => (
                    <div key={label} className="flex h-12 w-12 items-center justify-center border-r border-t border-[#484848]/30">
                        <span className="font-mono text-[10px] text-[#484848]">{label}</span>
                    </div>
                ))}
            </div>

            <main className="relative min-h-screen px-6 pb-20 pt-24">
                <div className="relative z-10 mx-auto grid w-full max-w-[68rem] grid-cols-1 items-center gap-6 md:grid-cols-12">
                    <div className="space-y-6 md:col-span-4">
                        <div className="space-y-4 border-l-2 border-[#8cacff] bg-[#131313] p-4">
                            <div className="flex items-center justify-between">
                                <span className="font-['Space_Grotesk'] text-xs tracking-widest text-[#8cacff]">SYSTEM_STATUS</span>
                                <span className="font-mono text-[10px] text-[#767575]">v4.2.0_STABLE_REF</span>
                            </div>
                            <div className="space-y-2">
                                {STATUS_ROWS.map((row) => (
                                    <div key={row.key}>
                                        <div className="mb-1 flex justify-between font-mono text-[11px]">
                                            <span className="text-[#acabaa]">{row.key}</span>
                                            <span className={row.color}>{row.value}</span>
                                        </div>
                                        <div className="h-1 w-full bg-[#252626]">
                                            <div
                                                className={`h-full ${row.color.replace('text-', 'bg-')}`}
                                                style={{ width: row.key === 'CORE_TEMP' ? '33%' : row.key === 'NODE_LOAD' ? '92%' : '68%' }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="relative aspect-square max-w-[17rem] overflow-hidden bg-[#131313] md:max-w-none">
                            <div className="absolute inset-0 flex items-center justify-center opacity-20">
                                <MaterialIcon icon="settings" size={180} className="animate-spin text-[#8cacff]" style={{ animationDuration: '20s' }} />
                            </div>
                            <div className="relative z-10 flex h-full flex-col items-center justify-center text-center">
                                <MaterialIcon icon="construction" size={48} className="mb-2 text-[#8cacff]" />
                                <div className="font-['Space_Grotesk'] text-2xl font-black tracking-tighter">REFACTORING</div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-8 bg-[#131313]/80 p-8 backdrop-blur-md md:col-span-8 md:min-h-[31rem] md:p-10">
                        <div className="space-y-2">
                            <div className="mb-4 flex items-center gap-2 font-mono text-xs text-[#00cffc]">
                                <span className="h-2 w-2 animate-pulse rounded-full bg-[#00cffc]" />
                                LIVE_PROTOCOL: MAINTENANCE_MODE
                            </div>
                            <h1 className="font-['Space_Grotesk'] text-[3.35rem] font-black leading-[0.96] tracking-[-0.05em] text-white md:text-[4.4rem]">
                                SYSTEM_UPGRADE
                                <br />
                                <span className="italic text-[#8cacff]">//</span> OFFLINE_REFACTORING
                            </h1>
                            <p className="max-w-xl pt-4 text-lg leading-relaxed text-[#acabaa]">
                                The core engine is currently undergoing a deep-sector refactor. We are realigning neural nodes to optimize system-wide throughput.
                            </p>
                        </div>

                        <div className="space-y-3">
                            <div className="flex justify-between font-['Space_Grotesk'] text-sm">
                                <span className="text-[#8cacff]">SEQUENCE_INITIALIZED</span>
                                <span className="text-[#00cffc]">99%</span>
                            </div>
                            <div className="group relative flex h-10 w-full items-center bg-[#252626] p-1.5">
                                <div className="relative flex h-full w-[99%] items-center justify-end overflow-hidden bg-gradient-to-r from-[#8cacff] to-[#00cffc] pr-2 shadow-[0_0_15px_rgba(140,172,255,0.4)]">
                                    <div className="absolute inset-0 opacity-20" style={{ background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, #000 10px, #000 20px)' }} />
                                    <span className="relative z-10 text-[10px] font-black uppercase tracking-widest text-[#001f55]">STABILIZING_FLUX</span>
                                </div>
                                <div className="absolute -right-2 top-0 flex h-full w-4 flex-col justify-around">
                                    <span className="h-1 w-1 animate-pulse bg-[#ff716c]" />
                                    <span className="h-1 w-1 animate-pulse bg-[#ff716c]" style={{ animationDelay: '0.2s' }} />
                                </div>
                            </div>
                            <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-widest text-[#ff716c]">
                                <MaterialIcon icon="warning" size={12} className="text-current" />
                                Error: CRC_MISMATCH at offset 0x00FF99 - Retrying sync...
                            </div>
                        </div>

                        <div className="flex flex-col items-center gap-8 pt-6 md:flex-row md:items-end">
                            <Link
                                to="/dashboard"
                                className="w-full bg-gradient-to-r from-[#8cacff] to-[#769dff] px-10 py-4 text-center font-['Space_Grotesk'] text-sm font-black uppercase tracking-widest text-[#001f55] shadow-[0_0_15px_rgba(140,172,255,0.4)] transition-transform hover:scale-105 active:scale-95 md:w-auto md:min-w-[12.5rem]"
                            >
                                RETURN TO BASE CAMP
                            </Link>
                            <div className="flex items-center gap-6 border-l border-[#484848]/30 pl-8">
                                <div>
                                    <div className="mb-1 font-['Space_Grotesk'] text-[10px] uppercase tracking-widest text-[#acabaa]">
                                        Estimated_Uptime
                                    </div>
                                    <div className="font-['Space_Grotesk'] text-3xl font-bold tracking-tighter text-[#00cffc]">
                                        00:14:<span className="text-[#8cacff]">59</span>
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <div className="h-8 w-1 bg-[#252626]" />
                                    <div className="h-8 w-1 bg-[#252626]" />
                                    <div className="h-8 w-1 animate-pulse bg-[#00cffc]" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <SiteFooter />
        </div>
    );
}
